import type { RoundOutcome, SessionAnalyticsWriter } from '../../sdk/mission';
import type { GameId, MissionId, PlayerId, SkillId } from '../../shared/types/ids';
import type { MissionProgressCounters } from '../../shared/types/mission-progress';
import type { PlayerParticipationRecord } from '../../shared/types/session';
import type { SkillEvidence } from '../../shared/types/skills';
import { createSkillEvidence } from '../skills/evidence';

/**
 * The session's analytics buffer.
 *
 * Everything a mission learns about a child accumulates here and is handed to the repositories
 * once, at the end, rather than dribbled into storage mid-round. Three rules are structural:
 *
 *   - **detector uncertainty is not evidence** (SKL-03, architecture §15). It has its own
 *     recording path and its own counters, and no code path moves a value from one to the other.
 *   - **a non-response is `skipped`, never `incorrect`** (architecture §12). A child who did not
 *     answer first, or at all, has not made a mistake.
 *   - nothing recorded here is a ranking. Participation is a count per player; there is no
 *     ordering field, no response-time comparison and no "first" flag anywhere in the shape.
 */

/** Platform-side extension: who matched inside the cooperative grace window (Phase 2). */
export interface MissionRoundOutcome extends RoundOutcome {
  graceParticipantIds?: readonly PlayerId[];
  /**
   * How much of the round's proactive, content-declared teaching moment actually ran (D-034
   * PS-5). Kept apart from `teachingUsed`, which means "this round needed help after a miss" and
   * therefore lowers the shard tier and marks the evidence assisted. A scripted moment is
   * teach-then-test: it happened, so a parent should see it counted, but the child got nothing
   * wrong and must not be scored as though they had.
   */
  scriptedTeachingDelivery?: 'FULL' | 'HINT' | 'NONE';
}

export interface NonResponseInput {
  playerIds: readonly PlayerId[];
  skillIds: readonly SkillId[];
  attemptNumber: number;
  teachingUsed: boolean;
}

export interface MissionAnalyticsWriter extends SessionAnalyticsWriter {
  recordRoundOutcome(outcome: MissionRoundOutcome): void;
  /**
   * Who joined in on a both-required celebration (COOP-06).
   *
   * Deliberately not a round outcome. A celebration is not a question, so putting it through
   * `recordRoundOutcome` would add it to "questions attempted" in a parent summary and tell a
   * grown-up their child answered fifteen questions in a fourteen-question mission. It writes
   * one neutral count and nothing else: no evidence, no score, no ordering.
   */
  recordBothRequiredMoment(input: { playerIds: readonly PlayerId[] }): void;
  /** Records "nobody answered" without recording a mistake. */
  recordNonResponse(input: NonResponseInput): void;
  evidence(): SkillEvidence[];
  roundOutcomes(): MissionRoundOutcome[];
  participationFor(playerId: PlayerId): PlayerParticipationRecord;
  participation(): PlayerParticipationRecord[];
  inputUncertaintyCount(playerId: PlayerId): number;
  questionsAttempted(): number;
  questionsCompleted(): number;
  /** Rounds whose answer needed more than one attempt — the parent summary's retry count. */
  retryCount(): number;
  teachingMomentCount(): number;
  /** The counters so far, for the interrupted-session snapshot (PRO-03). */
  counters(): MissionProgressCounters;
  /**
   * How many pieces of evidence this session has recorded per skill, earlier sittings included.
   *
   * `SkillEvidence` is written round by round and is durable, but it carries no session id — so a
   * session that resumes cannot go back and find its own earlier evidence. These counts travel in
   * the snapshot instead, which is what lets a resumed summary name every skill the evening
   * practised rather than only the ones after the interruption (R1-10).
   */
  skillPractice(): Record<SkillId, number>;
  /** Rehydrates the counters a previous sitting earned. */
  restore(counters: MissionProgressCounters): void;
  /** Rehydrates the per-skill practice counts a previous sitting earned. */
  restoreSkillPractice(practice: Readonly<Record<SkillId, number>>): void;
  /**
   * A content item this build could not present. Never academic: no evidence, no attempted
   * question, nothing a parent summary can read as something the child got wrong (R1-17).
   */
  recordContentDiagnostic(detail: { contentId: string; code: string }): void;
  contentDiagnostics(): readonly { contentId: string; code: string }[];
}

export interface SessionAnalyticsWriterOptions {
  gameId: GameId;
  missionId: MissionId;
  now?: () => Date;
  /** Players known at session start, so a summary lists a child who never answered. */
  playerIds?: readonly PlayerId[];
}

function emptyParticipation(playerId: PlayerId): PlayerParticipationRecord {
  return {
    playerId,
    responsesObserved: 0,
    participationWithinGrace: 0,
    bothRequiredCompletions: 0,
  };
}

export function createSessionAnalyticsWriter(
  options: SessionAnalyticsWriterOptions,
): MissionAnalyticsWriter {
  const now = options.now ?? (() => new Date());
  const evidence: SkillEvidence[] = [];
  const outcomes: MissionRoundOutcome[] = [];
  const participation = new Map<PlayerId, PlayerParticipationRecord>();
  /** Counts only. A trace of when a detector was unsure is a movement trace (architecture §19). */
  const uncertainty = new Map<PlayerId, number>();
  /** Per-skill practice counts, this sitting and every earlier one of the same session. */
  const practice = new Map<SkillId, number>();
  /** Content the build could not present. A diagnostic, never a child's record (R1-17). */
  const diagnostics: { contentId: string; code: string }[] = [];
  /** What a previous sitting of this same mission already earned (PRO-03). */
  let carried = {
    questionsAttempted: 0,
    questionsCompleted: 0,
    retryCount: 0,
    teachingMomentCount: 0,
  };

  for (const playerId of options.playerIds ?? []) {
    participation.set(playerId, emptyParticipation(playerId));
  }

  const participationFor = (playerId: PlayerId): PlayerParticipationRecord => {
    const existing = participation.get(playerId);
    if (existing !== undefined) return existing;
    const created = emptyParticipation(playerId);
    participation.set(playerId, created);
    return created;
  };

  const questionsAttempted = (): number => carried.questionsAttempted + outcomes.length;

  const questionsCompleted = (): number =>
    carried.questionsCompleted + outcomes.filter((outcome) => outcome.completed).length;

  const retryCount = (): number =>
    carried.retryCount +
    outcomes.reduce((total, outcome) => total + Math.max(0, outcome.attemptNumber - 1), 0);

  const teachingMomentCount = (): number =>
    carried.teachingMomentCount +
    outcomes.filter(
      (outcome) =>
        outcome.teachingUsed ||
        outcome.scriptedTeachingDelivery === 'FULL' ||
        outcome.scriptedTeachingDelivery === 'HINT',
    ).length;

  const countPractice = (entries: readonly SkillEvidence[]): void => {
    for (const entry of entries) {
      practice.set(entry.skillId, (practice.get(entry.skillId) ?? 0) + 1);
    }
  };

  return {
    recordEvidence(entries: SkillEvidence[]): void {
      evidence.push(...entries);
      countPractice(entries);
    },

    recordRoundOutcome(outcome: MissionRoundOutcome): void {
      outcomes.push(outcome);

      for (const playerId of outcome.observedPlayerIds) {
        const record = participationFor(playerId);
        record.responsesObserved += 1;
      }
      for (const playerId of outcome.graceParticipantIds ?? []) {
        participationFor(playerId).participationWithinGrace += 1;
      }
    },

    recordBothRequiredMoment(input: { playerIds: readonly PlayerId[] }): void {
      // One count per child per moment. A child named twice by a confused caller is still one
      // child who joined in once.
      for (const playerId of new Set(input.playerIds)) {
        participationFor(playerId).bothRequiredCompletions += 1;
      }
    },

    recordContentDiagnostic(detail: { contentId: string; code: string }): void {
      diagnostics.push({ ...detail });
    },

    contentDiagnostics: () => diagnostics.map((entry) => ({ ...entry })),

    recordNonResponse(input: NonResponseInput): void {
      const recordedAt = now().toISOString();
      const produced: SkillEvidence[] = [];
      for (const playerId of input.playerIds) {
        for (const skillId of input.skillIds) {
          produced.push(
            createSkillEvidence({
              playerId,
              gameId: options.gameId,
              missionId: options.missionId,
              skillId,
              // Never `incorrect`: silence is not a wrong answer (MVP notes §12, §13).
              result: 'skipped',
              attemptNumber: input.attemptNumber,
              teachingUsed: input.teachingUsed,
              recordedAt,
            }),
          );
        }
      }
      evidence.push(...produced);
      countPractice(produced);
    },

    recordInputUncertainty(
      playerId: PlayerId,
      detail: { capability: string; confidence: number },
    ): void {
      // The detail is read for its shape and deliberately not stored: what matters downstream is
      // that the session had uncertainty, not a per-frame record of when.
      void detail;
      uncertainty.set(playerId, (uncertainty.get(playerId) ?? 0) + 1);
    },

    evidence: () => [...evidence],

    roundOutcomes: () => [...outcomes],

    participationFor: (playerId) => ({ ...participationFor(playerId) }),

    participation: () => [...participation.values()].map((record) => ({ ...record })),

    inputUncertaintyCount: (playerId) => uncertainty.get(playerId) ?? 0,

    questionsAttempted,

    questionsCompleted,

    retryCount,

    teachingMomentCount,

    // Built from the same four getters the summary uses. A second copy of the arithmetic here
    // would let the snapshot and the summary drift apart without any test noticing.
    counters: (): MissionProgressCounters => ({
      questionsAttempted: questionsAttempted(),
      questionsCompleted: questionsCompleted(),
      retryCount: retryCount(),
      teachingMomentCount: teachingMomentCount(),
      participation: [...participation.values()].map((record) => ({ ...record })),
    }),

    skillPractice: (): Record<SkillId, number> => Object.fromEntries(practice),

    restoreSkillPractice(restored: Readonly<Record<SkillId, number>>): void {
      for (const [skillId, count] of Object.entries(restored)) {
        practice.set(skillId, Math.max(practice.get(skillId) ?? 0, count));
      }
    },

    restore(counters: MissionProgressCounters): void {
      carried = {
        questionsAttempted: counters.questionsAttempted,
        questionsCompleted: counters.questionsCompleted,
        retryCount: counters.retryCount,
        teachingMomentCount: counters.teachingMomentCount,
      };
      for (const record of counters.participation) {
        participation.set(record.playerId, { ...record });
      }
    },
  };
}
